import * as React from 'react';
import moment from 'moment-timezone';
import { DayPicker } from 'react-day-picker';

import { Button, ButtonGroup, IconButton, Popover } from '@/components/ui';

import {
    SHORTCUT_OPTIONS,
    addMonthsDate,
    canShiftInDirection,
    clampVisibleMonth,
    createCalendarDate,
    detectActiveShortcut,
    formatCalendarDateValue,
    formatCalendarMonthLabel,
    formatTriggerLabel,
    getShortcutRange,
    hasAnySixWeekMonth,
    isSameCalendarDay,
    isMonthAfter,
    shiftRange,
    startOfMonthDate,
    toDateObject
} from './utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CALENDAR_FONT_FAMILY = 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';

const DAY_PICKER_CLASS_NAMES = {
    caption: 'tw-hidden',
    caption_label: 'tw-text-sm tw-font-semibold tw-text-foreground',
    cell: 'tw-relative tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-p-0 tw-text-center tw-text-code [&:has([aria-selected].day-range-end)]:tw-rounded-r-md first:[&:has([aria-selected])]:tw-rounded-l-md last:[&:has([aria-selected])]:tw-rounded-r-md',
    day: 'tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-font-normal tw-text-code tw-text-foreground hover:tw-bg-muted focus:tw-outline-none',
    day_disabled: 'tw-text-muted-foreground tw-opacity-100 tw-pointer-events-none',
    day_hidden: 'tw-invisible',
    day_outside: 'tw-text-muted-foreground tw-opacity-100 tw-pointer-events-none',
    day_range_end: 'tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary hover:tw-text-primary-foreground focus:tw-bg-primary focus:tw-text-primary-foreground',
    day_range_middle: 'aria-selected:tw-bg-primary-surface aria-selected:tw-text-foreground',
    day_range_start: 'tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary hover:tw-text-primary-foreground focus:tw-bg-primary focus:tw-text-primary-foreground',
    day_selected: 'tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary hover:tw-text-primary-foreground focus:tw-bg-primary focus:tw-text-primary-foreground',
    day_today: 'tw-bg-muted tw-text-foreground',
    head_cell: 'tw-flex tw-h-5 tw-w-10 tw-items-center tw-justify-center tw-p-0 tw-text-center tw-text-caption tw-font-normal tw-uppercase tw-tracking-wide tw-text-muted-foreground',
    head_row: 'tw-flex tw-w-full',
    month: 'tw-w-[280px] tw-min-w-[280px] tw-max-w-[280px] tw-space-y-0',
    months: 'tw-grid tw-grid-cols-[280px_280px] tw-justify-center tw-items-start tw-gap-6',
    nav: 'tw-space-x-1 tw-flex tw-items-center',
    nav_button: 'tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-opacity-70 hover:tw-bg-muted hover:tw-opacity-100',
    nav_button_next: 'tw-absolute tw-right-1',
    nav_button_previous: 'tw-absolute tw-left-1',
    row: 'tw-flex tw-w-full tw-mt-1.5',
    table: 'tw-table-fixed tw-w-full tw-border-collapse tw-space-y-1'
};

const DAY_PICKER_SINGLE_CLASS_NAMES = {
    ...DAY_PICKER_CLASS_NAMES,
    months: 'tw-grid tw-grid-cols-[280px] tw-justify-center tw-items-start'
};

const DAY_PICKER_MODIFIER_STYLES = {
    disabled: {
        color: 'rgb(203, 213, 225)',
        cursor: 'not-allowed',
        opacity: 1,
        pointerEvents: 'none'
    },
    range_end: {
        backgroundColor: 'rgb(139, 197, 63)',
        color: 'rgb(248, 250, 252)'
    },
    range_middle: {
        backgroundColor: 'rgba(139, 197, 63, 0.14)',
        color: 'rgb(51, 65, 85)'
    },
    range_start: {
        backgroundColor: 'rgb(139, 197, 63)',
        color: 'rgb(248, 250, 252)'
    },
    selected: {
        backgroundColor: 'rgb(139, 197, 63)',
        color: 'rgb(248, 250, 252)'
    }
};

const TABLE_RESET_CSS = `
    .wm-calendar-shell table,
    .wm-calendar-shell thead,
    .wm-calendar-shell tbody,
    .wm-calendar-shell tr,
    .wm-calendar-shell th,
    .wm-calendar-shell td {
        border: 0 !important;
        box-shadow: none !important;
        padding: 0 !important;
    }
`;

// ---------------------------------------------------------------------------
// Visible-month initialiser
// ---------------------------------------------------------------------------

const getInitialVisibleMonth = (value, minVisibleMonth, maxVisibleMonth) => {
    const dateStr = typeof value === 'object' ? value?.start : value;
    const initialDate = toDateObject(dateStr) || new Date();

    return clampVisibleMonth(initialDate, minVisibleMonth, maxVisibleMonth);
};

// ---------------------------------------------------------------------------
// Calendar component
// ---------------------------------------------------------------------------

// Trigger wrapper width grows with size so the formatted date range (which
// pulls in tabular-num digits and a separator) fits without truncation at
// any control-height stop. The matching IconButton width feeds the popover
// alignment offset so the dropdown anchors to the leading shift chevron.
const TRIGGER_WIDTH_BY_SIZE = {
    small: 'tw-w-48',
    medium: 'tw-w-56',
    large: 'tw-w-60'
};
const ICONBUTTON_WIDTH_BY_SIZE = {
    small: 32,
    medium: 40,
    large: 44
};

const Calendar = ({
    disabled = false,
    maxDate,
    minDate,
    mode = 'single',
    onChange,
    shortcuts = false,
    shiftable = false,
    size = 'medium',
    timezone,
    value,
    variant = 'simple'
}) => {
    const isRange = mode === 'range';

    // -- Derived date boundaries ------------------------------------------------

    const todayDate = React.useMemo(() => {
        const today = moment.tz(timezone);
        return createCalendarDate(today.year(), today.month(), today.date());
    }, [timezone]);

    const minimumSelectableDate = React.useMemo(() => toDateObject(minDate), [minDate]);
    const maximumSelectableDate = React.useMemo(() => toDateObject(maxDate), [maxDate]);

    const minVisibleMonth = React.useMemo(
        () => minimumSelectableDate ? startOfMonthDate(minimumSelectableDate) : startOfMonthDate(new Date(2000, 0, 1)),
        [minimumSelectableDate]
    );

    const todayMonth = React.useMemo(() => startOfMonthDate(todayDate), [todayDate]);

    const maxVisibleMonth = React.useMemo(
        () => isRange ? addMonthsDate(todayMonth, -1) : todayMonth,
        [isRange, todayMonth]
    );

    const needsSixWeekHeight = React.useMemo(
        () => hasAnySixWeekMonth(minDate, maxDate),
        [minDate, maxDate]
    );

    const dayPickerClassNames = React.useMemo(() => {
        const base = isRange ? DAY_PICKER_CLASS_NAMES : DAY_PICKER_SINGLE_CLASS_NAMES;

        if (!needsSixWeekHeight) {
            return base;
        }

        return {
            ...base,
            table: `${base.table} tw-min-h-[296px]`
        };
    }, [isRange, needsSixWeekHeight]);

    // -- Popover state ----------------------------------------------------------

    const [open, setOpen] = React.useState(false);
    const [hasInteracted, setHasInteracted] = React.useState(false);
    const closeTimeoutRef = React.useRef(null);
    const triggerHoveredRef = React.useRef(false);
    const contentHoveredRef = React.useRef(false);

    // -- Draft state (range mode) -----------------------------------------------

    const rangeStart = isRange ? value?.start : undefined;
    const rangeFinish = isRange ? value?.finish : undefined;

    const [draftRange, setDraftRange] = React.useState({
        from: toDateObject(rangeStart),
        to: toDateObject(rangeFinish)
    });

    // -- Draft state (single mode) -----------------------------------------------

    const [draftSingle, setDraftSingle] = React.useState(toDateObject(isRange ? undefined : value));

    // -- Visible month ----------------------------------------------------------

    const [visibleMonth, setVisibleMonth] = React.useState(
        () => getInitialVisibleMonth(value, minVisibleMonth, maxVisibleMonth)
    );

    // -- Sync draft when value/open changes -------------------------------------

    React.useEffect(() => {
        if (!open) {
            setHasInteracted(false);

            if (isRange) {
                setDraftRange({
                    from: toDateObject(rangeStart),
                    to: toDateObject(rangeFinish)
                });
            } else {
                setDraftSingle(toDateObject(value));
            }

            setVisibleMonth(getInitialVisibleMonth(value, minVisibleMonth, maxVisibleMonth));
        }
    }, [isRange, maxVisibleMonth, minVisibleMonth, open, rangeFinish, rangeStart, value]);

    // -- Cleanup timer on unmount -----------------------------------------------

    React.useEffect(() => () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
    }, []);

    // -- Range draft helpers ----------------------------------------------------

    const committedEndDate = React.useMemo(() => toDateObject(rangeFinish), [rangeFinish]);

    const effectiveDraftEndDate = React.useMemo(() => {
        if (draftRange?.to) {
            return draftRange.to;
        }

        if (draftRange?.from && committedEndDate && draftRange.from < committedEndDate) {
            return committedEndDate;
        }

        return undefined;
    }, [committedEndDate, draftRange]);

    const isDraftValid = isRange
        ? (!!draftRange?.from && !!effectiveDraftEndDate)
        : !!draftSingle;

    // -- Visible month navigation state -----------------------------------------

    const leftDisplayMonth = visibleMonth;
    const rightDisplayMonth = React.useMemo(() => addMonthsDate(visibleMonth, 1), [visibleMonth]);

    const isAtMaxVisibleMonth = React.useMemo(() => (
        visibleMonth.getFullYear() === maxVisibleMonth.getFullYear()
            && visibleMonth.getMonth() === maxVisibleMonth.getMonth()
    ), [maxVisibleMonth, visibleMonth]);

    const isAtMinVisibleMonth = React.useMemo(() => (
        visibleMonth.getFullYear() === minVisibleMonth.getFullYear()
            && visibleMonth.getMonth() === minVisibleMonth.getMonth()
    ), [minVisibleMonth, visibleMonth]);

    const canGoBackward = React.useMemo(
        () => !isMonthAfter(minVisibleMonth, addMonthsDate(visibleMonth, -1)),
        [minVisibleMonth, visibleMonth]
    );

    const canGoForward = React.useMemo(
        () => !isMonthAfter(addMonthsDate(visibleMonth, isRange ? 1 : 0), maxVisibleMonth),
        [isRange, maxVisibleMonth, visibleMonth]
    );

    const canGoBackwardYear = !isAtMinVisibleMonth;
    const canGoForwardYear = !isAtMaxVisibleMonth;

    // -- Shift period state (range only) ----------------------------------------

    const canShiftBackward = React.useMemo(() => {
        if (!isRange || !shiftable || !rangeStart || !rangeFinish) {
            return false;
        }

        return canShiftInDirection({
            direction: -1,
            finish: rangeFinish,
            maxDate,
            minDate,
            start: rangeStart,
            timezone
        });
    }, [isRange, maxDate, minDate, rangeFinish, rangeStart, shiftable, timezone]);

    const canShiftForward = React.useMemo(() => {
        if (!isRange || !shiftable || !rangeStart || !rangeFinish) {
            return false;
        }

        return canShiftInDirection({
            direction: 1,
            finish: rangeFinish,
            maxDate,
            minDate,
            start: rangeStart,
            timezone
        });
    }, [isRange, maxDate, minDate, rangeFinish, rangeStart, shiftable, timezone]);

    // -- Active shortcut detection ----------------------------------------------

    const activeShortcut = React.useMemo(() => {
        if (!isRange || !shortcuts || !rangeStart || !rangeFinish) {
            return null;
        }

        return detectActiveShortcut({
            finish: rangeFinish,
            maxDate,
            minDate,
            start: rangeStart,
            timezone
        });
    }, [isRange, maxDate, minDate, rangeFinish, rangeStart, shortcuts, timezone]);

    // -- Callbacks: popover hover-to-open ---------------------------------------

    const cancelScheduledClose = React.useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }, []);

    const scheduleCloseIfIdle = React.useCallback(() => {
        cancelScheduledClose();

        closeTimeoutRef.current = setTimeout(() => {
            if (!triggerHoveredRef.current && !contentHoveredRef.current && !hasInteracted) {
                setOpen(false);
            }
        }, 120);
    }, [cancelScheduledClose, hasInteracted]);

    const forceClosePopover = React.useCallback(() => {
        cancelScheduledClose();
        triggerHoveredRef.current = false;
        contentHoveredRef.current = false;
        setHasInteracted(false);
        setOpen(false);
    }, [cancelScheduledClose]);

    React.useEffect(() => {
        if (disabled && open) {
            forceClosePopover();
        }
    }, [disabled, forceClosePopover, open]);

    const handleTriggerMouseEnter = React.useCallback(() => {
        triggerHoveredRef.current = true;
        cancelScheduledClose();
        setOpen(true);
    }, [cancelScheduledClose]);

    const handleTriggerMouseLeave = React.useCallback(() => {
        triggerHoveredRef.current = false;
        scheduleCloseIfIdle();
    }, [scheduleCloseIfIdle]);

    const handleContentMouseEnter = React.useCallback(() => {
        contentHoveredRef.current = true;
        cancelScheduledClose();
    }, [cancelScheduledClose]);

    const handleContentMouseLeave = React.useCallback(() => {
        contentHoveredRef.current = false;
        scheduleCloseIfIdle();
    }, [scheduleCloseIfIdle]);

    // -- Callbacks: commit & shift ----------------------------------------------

    const commitRangeDraft = React.useCallback(() => {
        if (!draftRange?.from || !effectiveDraftEndDate) {
            return;
        }

        onChange?.({
            start: formatCalendarDateValue(draftRange.from),
            finish: formatCalendarDateValue(effectiveDraftEndDate)
        });
        setOpen(false);
    }, [draftRange, effectiveDraftEndDate, onChange]);

    const handleShiftPeriod = React.useCallback(direction => {
        if (!isRange || !shiftable || !rangeStart || !rangeFinish) {
            return;
        }

        const shifted = shiftRange({
            direction,
            finish: rangeFinish,
            maxDate,
            minDate,
            start: rangeStart,
            timezone
        });

        forceClosePopover();
        onChange?.(shifted);
    }, [forceClosePopover, isRange, maxDate, minDate, onChange, rangeFinish, rangeStart, shiftable, timezone]);

    // -- Callbacks: day click ---------------------------------------------------

    const handleRangeDayClick = React.useCallback((day, modifiers) => {
        if (modifiers?.outside || modifiers?.disabled) {
            return;
        }

        setHasInteracted(true);

        if (!draftRange?.from || draftRange?.to) {
            setDraftRange({ from: day, to: undefined });
            return;
        }

        if (isSameCalendarDay(day, draftRange.from)) {
            setDraftRange({ from: day, to: day });
            return;
        }

        setDraftRange(day < draftRange.from
            ? { from: day, to: draftRange.from }
            : { from: draftRange.from, to: day }
        );
    }, [draftRange]);

    const handleSingleDayClick = React.useCallback((day, modifiers) => {
        if (modifiers?.outside || modifiers?.disabled) {
            return;
        }

        setHasInteracted(true);
        setDraftSingle(day);
        onChange?.(formatCalendarDateValue(day));
        setOpen(false);
    }, [onChange]);

    // -- Callbacks: month shift (inside calendar grid) --------------------------

    const shiftCalendarMonths = React.useCallback(amount => {
        if (disabled) {
            return;
        }

        setHasInteracted(true);
        setVisibleMonth(current => clampVisibleMonth(addMonthsDate(current, amount), minVisibleMonth, maxVisibleMonth));
    }, [disabled, maxVisibleMonth, minVisibleMonth]);

    // -- DayPicker modifiers (range) --------------------------------------------

    const draftStartDate = draftRange?.from;
    const draftEndDate = effectiveDraftEndDate;

    const rangeDayPickerModifiers = React.useMemo(() => ({
        range_end: day => isSameCalendarDay(day, draftEndDate),
        range_middle: day => {
            if (!draftStartDate || !draftEndDate) {
                return false;
            }

            return day > draftStartDate && day < draftEndDate;
        },
        range_start: day => isSameCalendarDay(day, draftStartDate),
        selected: day => isSameCalendarDay(day, draftStartDate) || isSameCalendarDay(day, draftEndDate)
    }), [draftEndDate, draftStartDate]);

    // -- DayPicker modifiers (single) -------------------------------------------

    const singleDayPickerModifiers = React.useMemo(() => ({
        selected: day => isSameCalendarDay(day, draftSingle)
    }), [draftSingle]);

    // -- Trigger label ----------------------------------------------------------

    const triggerLabel = React.useMemo(() => {
        if (isRange) {
            return formatTriggerLabel(rangeStart || '', rangeFinish || '');
        }

        return value || '';
    }, [isRange, rangeFinish, rangeStart, value]);

    // -- Popover onOpenChange ---------------------------------------------------

    const handleOpenChange = React.useCallback(nextOpen => {
        if (disabled) {
            return;
        }

        if (!nextOpen && (triggerHoveredRef.current || contentHoveredRef.current)) {
            return;
        }

        if (!nextOpen) {
            cancelScheduledClose();
            triggerHoveredRef.current = false;
            contentHoveredRef.current = false;
        }

        setOpen(nextOpen);
    }, [cancelScheduledClose, disabled]);

    // -- Render: month navigation header ----------------------------------------

    const renderMonthNavigation = () => {
        if (isRange) {
            return (
                <div className="tw-mb-3 tw-grid tw-grid-cols-2 tw-justify-center tw-gap-6">
                    <div className="tw-flex tw-w-72 tw-items-center">
                        <div className="tw-inline-flex tw-items-center tw-gap-0">
                            <IconButton
                                aria-label={i18next.t('ui.calendar.previous_year', 'Previous year')}
                                disabled={disabled || !canGoBackwardYear}
                                icon="chevronsLeft"
                                onClick={() => shiftCalendarMonths(-12)}
                                size="small"
                            />
                            <IconButton
                                aria-label={i18next.t('ui.calendar.previous_month', 'Previous month')}
                                disabled={disabled || !canGoBackward}
                                icon="chevronLeft"
                                onClick={() => shiftCalendarMonths(-1)}
                                size="small"
                            />
                        </div>
                        <div className="tw-inline-flex tw-flex-1 tw-items-center tw-justify-center tw-px-1.5 tw-py-1 tw-text-sm tw-font-semibold tw-text-foreground">
                            <span>{formatCalendarMonthLabel(leftDisplayMonth, timezone)}</span>
                        </div>
                    </div>
                    <div className="tw-flex tw-w-72 tw-items-center">
                        <div className="tw-inline-flex tw-flex-1 tw-items-center tw-justify-center tw-px-1.5 tw-py-1 tw-text-sm tw-font-semibold tw-text-foreground">
                            <span>{formatCalendarMonthLabel(rightDisplayMonth, timezone)}</span>
                        </div>
                        <div className="tw-inline-flex tw-items-center tw-gap-0">
                            <IconButton
                                aria-label={i18next.t('ui.calendar.next_month', 'Next month')}
                                disabled={disabled || !canGoForward}
                                icon="chevronRight"
                                onClick={() => shiftCalendarMonths(1)}
                                size="small"
                            />
                            <IconButton
                                aria-label={i18next.t('ui.calendar.next_year', 'Next year')}
                                disabled={disabled || !canGoForwardYear}
                                icon="chevronsRight"
                                onClick={() => shiftCalendarMonths(12)}
                                size="small"
                            />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="tw-mb-3 tw-flex tw-w-72 tw-items-center">
                <div className="tw-inline-flex tw-items-center tw-gap-0">
                    <IconButton
                        aria-label={i18next.t('ui.calendar.previous_year', 'Previous year')}
                        disabled={disabled || !canGoBackwardYear}
                        icon="chevronsLeft"
                        onClick={() => shiftCalendarMonths(-12)}
                        size="medium"
                    />
                    <IconButton
                        aria-label={i18next.t('ui.calendar.previous_month', 'Previous month')}
                        disabled={disabled || !canGoBackward}
                        icon="chevronLeft"
                        onClick={() => shiftCalendarMonths(-1)}
                        size="medium"
                    />
                </div>
                <div className="tw-inline-flex tw-flex-1 tw-items-center tw-justify-center tw-px-1.5 tw-py-1 tw-text-sm tw-font-semibold tw-text-foreground">
                    <span>{formatCalendarMonthLabel(leftDisplayMonth, timezone)}</span>
                </div>
                <div className="tw-inline-flex tw-items-center tw-gap-0">
                    <IconButton
                        aria-label={i18next.t('ui.calendar.next_month', 'Next month')}
                        disabled={disabled || !canGoForward}
                        icon="chevronRight"
                        onClick={() => shiftCalendarMonths(1)}
                        size="medium"
                    />
                    <IconButton
                        aria-label={i18next.t('ui.calendar.next_year', 'Next year')}
                        disabled={disabled || !canGoForwardYear}
                        icon="chevronsRight"
                        onClick={() => shiftCalendarMonths(12)}
                        size="medium"
                    />
                </div>
            </div>
        );
    };

    // -- Render: shortcuts bar (range only) -------------------------------------

    const renderShortcuts = () => {
        if (!isRange || !shortcuts) {
            return null;
        }

        return (
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-t tw-border-border tw-px-4 tw-py-3">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    {SHORTCUT_OPTIONS.map(option => (
                        <Button
                            appearance={option.value === activeShortcut ? 'solid' : 'outline'}
                            disabled={disabled}
                            key={option.value}
                            onClick={() => {
                                const range = getShortcutRange(option.value, timezone, { maxDate, minDate });
                                setHasInteracted(true);
                                onChange?.(range);
                                setOpen(false);
                            }}
                            size="small"
                            tone={option.value === activeShortcut ? 'primary' : 'neutral'}
                            type="button"
                        >
                            {i18next.t(option.labelKey)}
                        </Button>
                    ))}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-min-w-14">
                        <Button
                            disabled={disabled || !isDraftValid}
                            fullWidth
                            onClick={commitRangeDraft}
                            size="small"
                            type="button"
                        >
                            {i18next.t('ui.buttons.ok')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    // -- Render: popover content ------------------------------------------------

    const panelWidth = isRange ? 640 : 340;

    const renderCalendarPanel = () => (
        <div className="wm-calendar-shell tw-grid tw-gap-0">
            <style>{TABLE_RESET_CSS}</style>
            <div className="tw-relative tw-p-3">
                {renderMonthNavigation()}
                <DayPicker
                    className="tw-p-0"
                    classNames={dayPickerClassNames}
                    components={{
                        IconLeft: () => null,
                        IconRight: () => null
                    }}
                    disabled={{
                        after: maximumSelectableDate || todayDate,
                        before: minimumSelectableDate
                    }}
                    modifiers={isRange ? rangeDayPickerModifiers : singleDayPickerModifiers}
                    modifiersStyles={DAY_PICKER_MODIFIER_STYLES}
                    month={visibleMonth}
                    numberOfMonths={isRange ? 2 : 1}
                    onDayClick={isRange ? handleRangeDayClick : handleSingleDayClick}
                    onMonthChange={month => {
                        if (disabled) {
                            return;
                        }

                        setHasInteracted(true);
                        setVisibleMonth(clampVisibleMonth(month, minVisibleMonth, maxVisibleMonth));
                    }}
                    toMonth={maximumSelectableDate || todayDate}
                    today={todayDate}
                />
            </div>
            {renderShortcuts()}
        </div>
    );

    // -- Render: trigger --------------------------------------------------------

    const renderTrigger = () => (
        <Button
            appearance="outline"
            disabled={disabled}
            fullWidth
            onMouseEnter={handleTriggerMouseEnter}
            onMouseLeave={handleTriggerMouseLeave}
            size={size}
            tone="neutral"
            type="button"
        >
            <span
                className="tw-w-full tw-text-center"
                style={{ fontFamily: CALENDAR_FONT_FAMILY, fontVariantNumeric: 'tabular-nums' }}
            >
                {triggerLabel}
            </span>
        </Button>
    );

    // -- Render: main -----------------------------------------------------------
    //
    // One composition for every Calendar mode. Shift chevrons are an
    // optional pair around the trigger, not a different return path.
    // ButtonGroup with a single child resolves to position='only', which
    // applies no corner-flatten — so the standalone trigger looks
    // identical to a bare Button while keeping the structure consistent.
    const showShiftControls = isRange && shiftable;

    return (
        <ButtonGroup aria-label={i18next.t('common.interval')}>
            {showShiftControls ? (
                <IconButton
                    appearance="outline"
                    aria-label={i18next.t('indicators.chart.previous_period')}
                    disabled={disabled || !canShiftBackward}
                    icon="chevronLeft"
                    onClick={() => handleShiftPeriod(-1)}
                    size={size}
                    tone="neutral"
                />
            ) : null}
            <div className={showShiftControls ? `${TRIGGER_WIDTH_BY_SIZE[size]} tw-flex-none` : undefined}>
                <Popover
                    align="start"
                    alignOffset={showShiftControls ? -ICONBUTTON_WIDTH_BY_SIZE[size] : 0}
                    onOpenChange={handleOpenChange}
                    open={open}
                    preventAutoFocus
                    sideOffset={8}
                    trigger={renderTrigger()}
                    content={
                        <div
                            onMouseEnter={handleContentMouseEnter}
                            onMouseLeave={handleContentMouseLeave}
                            style={{ fontFamily: CALENDAR_FONT_FAMILY, width: `${panelWidth}px` }}
                        >
                            {renderCalendarPanel()}
                        </div>
                    }
                />
            </div>
            {showShiftControls ? (
                <IconButton
                    appearance="outline"
                    aria-label={i18next.t('indicators.chart.next_period')}
                    disabled={disabled || !canShiftForward}
                    icon="chevronRight"
                    onClick={() => handleShiftPeriod(1)}
                    size={size}
                    tone="neutral"
                />
            ) : null}
        </ButtonGroup>
    );
};

Calendar.displayName = 'Calendar';

export { Calendar };
